type AppConfig = {
  environment: 'production' | 'staging' | 'performance' | 'development';
  mapboxToken: string;
  oauthClientId: string;
  appUrl: string;
  authUrl: string;
  oauthIdentityPath: string;
  sentryDsn?: string;
  tagManagerId: string;
  intercomId: string;
  enableWorktypeAnalysis: boolean;
  launchDarkly: string;
  launchDarklyLocalUri: string;
  descopeProjectId: string;
  descopeLoginFlowId: string;
  descopeAcceptInviteFlowId: string;
};

// A path value (e.g. "/") points the LD SDK at the serving origin, whose dev
// server proxies the LD client endpoints. The origin can't be hardcoded
// because the page may be reached through different host ports.
const resolveLaunchDarklyLocalUri = (uri: string): string =>
  uri.startsWith('/') ? window.location.origin : uri;

export const getAppConfig = (): AppConfig => {
  const host = window.location.host;

  const defaultConfig = {
    oauthIdentityPath: '?gcp-iap-mode=IDENTITY',
    tagManagerId: 'GTM-W32G85Z',
    enableWorktypeAnalysis: true,
  };

  if (host === 'app.us.overstory.com') {
    // US Production
    return {
      ...defaultConfig,
      environment: 'production',
      mapboxToken:
        'pk.eyJ1Ijoib3ZlcnN0b3J5IiwiYSI6ImNtOWl3aHczZjA1M3cybHBuYzZyZXRmMGoifQ.jj5HDnpNJoyiFmXfbANX0g',
      appUrl: 'https://app.us.overstory.com',
      authUrl: 'https://auth.app.us.overstory.com',
      oauthClientId:
        '756301310944-27t88n5q8md1t8tj02qeh490nr94cd4p.apps.googleusercontent.com',
      sentryDsn:
        'https://f3faafc5e54049d6ab653939e0171a4a@o1185153.ingest.sentry.io/6303582',
      intercomId: 'z48gkwms',
      launchDarkly: '67aa1eb6f67eab09ce4387a8',
      launchDarklyLocalUri: '',
      descopeLoginFlowId: 'overstory-sign-in-with-sso-magic-link',
      descopeProjectId: 'P3BOK5iyg398g5QGNbZsAYF4qzez',
      descopeAcceptInviteFlowId: 'overstory-accept-invite',
    };
  } else if (host === 'stage-app.us.overstory.com') {
    // US Staging
    return {
      ...defaultConfig,
      environment: 'staging',
      mapboxToken:
        'pk.eyJ1Ijoib3ZlcnN0b3J5IiwiYSI6ImNtOWl3aDFwOTA1NHQyd3E0cTJxbHg1MGQifQ.ekbKAqPFeBf-304RFioYmw',
      appUrl: 'https://stage-app.us.overstory.com',
      authUrl: 'https://auth.stage-app.us.overstory.com',
      oauthClientId:
        '756301310944-8jb4sj2ehe2lbk97ls4h7a4uman61kln.apps.googleusercontent.com',
      sentryDsn:
        'https://f3faafc5e54049d6ab653939e0171a4a@o1185153.ingest.sentry.io/6303582',
      intercomId: 'gw1zoo8i',
      launchDarkly: '67ae1a496b58f10b9bd684b3',
      launchDarklyLocalUri: '',
      descopeLoginFlowId: 'overstory-sign-in-with-sso-magic-link',
      descopeAcceptInviteFlowId: 'overstory-accept-invite',
      descopeProjectId: 'P3BOJflSS4fY5xRvioiGBwsor5nr',
    };
  } else if (host === 'perf-app.us.overstory.com') {
    // US Performance
    return {
      ...defaultConfig,
      environment: 'performance',
      mapboxToken:
        'pk.eyJ1Ijoib3ZlcnN0b3J5IiwiYSI6ImNtOWl1bHNzbTA2ZXMyanBvem5zNTE1bGgifQ.dKUfldtp-SHlWY7JmIQ8kg',
      appUrl: 'https://perf-app.us.overstory.com',
      authUrl: 'https://auth.perf-app.us.overstory.com',
      oauthClientId:
        '756301310944-8jb4sj2ehe2lbk97ls4h7a4uman61kln.apps.googleusercontent.com',
      sentryDsn:
        'https://f3faafc5e54049d6ab653939e0171a4a@o1185153.ingest.sentry.io/6303582',
      intercomId: 'gw1zoo8i',
      launchDarkly: '67ae2cd57532e7096de29b00',
      launchDarklyLocalUri: '',
      descopeLoginFlowId: 'overstory-sign-in-with-sso-magic-link',
      descopeAcceptInviteFlowId: 'overstory-accept-invite',
      descopeProjectId: 'P3B5IuG6QqgXlPC6koEFbbQE41RZ',
    };
  } else if (host === 'app.overstory.com') {
    // EU Production
    return {
      ...defaultConfig,
      environment: 'production',
      mapboxToken:
        'pk.eyJ1Ijoib3ZlcnN0b3J5IiwiYSI6ImNtOWl3aHczZjA1M3cybHBuYzZyZXRmMGoifQ.jj5HDnpNJoyiFmXfbANX0g',
      appUrl: 'https://app.overstory.com',
      authUrl: 'https://auth.app.overstory.com',
      oauthClientId:
        '756301310944-27t88n5q8md1t8tj02qeh490nr94cd4p.apps.googleusercontent.com',
      sentryDsn:
        'https://f3faafc5e54049d6ab653939e0171a4a@o1185153.ingest.sentry.io/6303582',
      intercomId: 'z48gkwms',
      launchDarkly: '67aa1eb6f67eab09ce4387a8',
      launchDarklyLocalUri: '',
      descopeLoginFlowId: 'overstory-sign-in-with-sso-magic-link',
      descopeProjectId: 'P38nwrWGjD768clgPFODdrUwYY0G',
      descopeAcceptInviteFlowId: 'overstory-accept-invite',
    };
  } else if (host === 'perf-app.overstory.com') {
    return {
      ...defaultConfig,
      environment: 'performance',
      mapboxToken:
        'pk.eyJ1Ijoib3ZlcnN0b3J5IiwiYSI6ImNtOWl1bHNzbTA2ZXMyanBvem5zNTE1bGgifQ.dKUfldtp-SHlWY7JmIQ8kg',
      appUrl: 'https://perf-app.overstory.com',
      authUrl: 'https://auth.perf-app.overstory.com',
      oauthClientId:
        '756301310944-8jb4sj2ehe2lbk97ls4h7a4uman61kln.apps.googleusercontent.com',
      sentryDsn:
        'https://f3faafc5e54049d6ab653939e0171a4a@o1185153.ingest.sentry.io/6303582',
      intercomId: 'gw1zoo8i', // Test intercom workspace (same as stage)
      launchDarkly: '67ae2cd57532e7096de29b00',
      launchDarklyLocalUri: '',
      descopeLoginFlowId: 'overstory-sign-in-with-sso-magic-link',
      descopeAcceptInviteFlowId: 'overstory-accept-invite',
      descopeProjectId: 'P38ffXSq60YFLXH4RXh0IvZBsM3t',
    };
  } else if (host === 'stage-app.overstory.com') {
    return {
      ...defaultConfig,
      environment: 'staging',
      mapboxToken:
        'pk.eyJ1Ijoib3ZlcnN0b3J5IiwiYSI6ImNtOWl3aDFwOTA1NHQyd3E0cTJxbHg1MGQifQ.ekbKAqPFeBf-304RFioYmw',
      appUrl: 'https://stage-app.overstory.com',
      authUrl: 'https://auth.stage-app.overstory.com',
      oauthClientId:
        '756301310944-8jb4sj2ehe2lbk97ls4h7a4uman61kln.apps.googleusercontent.com',
      sentryDsn:
        'https://f3faafc5e54049d6ab653939e0171a4a@o1185153.ingest.sentry.io/6303582',
      intercomId: 'gw1zoo8i',
      launchDarkly: '67ae1a496b58f10b9bd684b3',
      launchDarklyLocalUri: '',
      descopeLoginFlowId: 'overstory-sign-in-with-sso-magic-link',
      descopeAcceptInviteFlowId: 'overstory-accept-invite',
      descopeProjectId: 'P38nwR2NniUJR1GkVkeBJoiYycYV',
    };
  }

  return {
    ...defaultConfig,
    environment: 'development',
    mapboxToken:
      import.meta.env.VITE_MAPBOX_TOKEN ??
      'pk.eyJ1Ijoib3ZlcnN0b3J5IiwiYSI6ImNtY203cHl6dzBqZHYya3ExM2IybXVnczkifQ.gN4uqrJOsjw-MUCF0nbgoQ',
    oauthClientId: import.meta.env.VITE_OAUTH_CLIENT_ID ?? '',
    appUrl: 'http://localhost:3000',
    authUrl: '',
    oauthIdentityPath: '/identity',
    intercomId: import.meta.env.VITE_INTERCOM_ID ?? '',
    launchDarkly: import.meta.env.VITE_LAUNCH_DARKLY_CLIENT_ID ?? '',
    launchDarklyLocalUri: resolveLaunchDarklyLocalUri(
      import.meta.env.VITE_LAUNCH_DARKLY_LOCAL_URI ?? ''
    ),
    descopeProjectId: import.meta.env.VITE_DESCOPE_PROJECT_ID ?? '',
    descopeLoginFlowId: 'overstory-sign-in-dev-only',
    descopeAcceptInviteFlowId: 'overstory-accept-invite',
  };
};
